import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { adminService } from "../lib/adminService";
import { SolicitationsList } from "../components/SolicitationsList";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LogOut, Home } from "lucide-react";

export function AdminPanel() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [solicitations, setSolicitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    loadSolicitations();
  }, [user]);

  const loadSolicitations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getSolicitations();
      setSolicitations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading solicitations:", err);
      setError("Erro ao carregar solicitações");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuotation = async (quotationData) => {
    try {
      await adminService.createQuotation(quotationData);
      // Refresh solicitations list
      await loadSolicitations();
    } catch (err) {
      console.error("Error creating quotation:", err);
      setError("Erro ao enviar cotação");
    }
  };

  const handleApprove = async (solicitationId) => {
    try {
      await adminService.approveSolicitation({ id: solicitationId });
      await loadSolicitations();
    } catch (err) {
      console.error("Error approving solicitation:", err);
      setError("Erro ao aprovar solicitação");
    }
  };

  const handleReject = async (solicitationId) => {
    const reason = window.prompt("Motivo da rejeição:");
    if (reason) {
      try {
        await adminService.rejectSolicitation({ id: solicitationId, reason });
        await loadSolicitations();
      } catch (err) {
        console.error("Error rejecting solicitation:", err);
        setError("Erro ao rejeitar solicitação");
      }
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-vitapink-100 via-white to-gray-50 flex items-center justify-center">
        <p className="text-lg text-gray-600">Carregando painel...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-vitapink-100 via-white to-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Painel de Administração
              </h1>
              <p className="text-sm text-gray-600 mt-1">{user?.email}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate("/dashboard")}>
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <Button variant="outline" onClick={handleSignOut}>
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-800">{error}</p>
            </CardContent>
          </Card>
        )}

        <div className="mb-6">
          <Card>
            <CardHeader>
              <CardTitle>Resumo</CardTitle>
              <CardDescription>Estatísticas gerais do sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Total de Solicitações</p>
                  <p className="text-2xl font-bold text-vitapink-600">
                    {solicitations.length}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pendentes</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {solicitations.filter((s) => s.status === "pending").length}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Aprovadas</p>
                  <p className="text-2xl font-bold text-green-600">
                    {
                      solicitations.filter((s) => s.status === "approved")
                        .length
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">com Cotação</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {
                      solicitations.filter((s) => s.status === "quotation_sent")
                        .length
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Solicitations List */}
        <SolicitationsList
          solicitations={solicitations}
          onCreateQuotation={handleCreateQuotation}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      </div>
    </div>
  );
}
